"use client";

import { useSuspenseQuery } from "@apollo/client/react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import { getUrls } from "@portal/app/_common/lib/urls";
import { useRouter } from "next/navigation";
import type { BlogPageProps } from "@portal/app/blog/[slug]/page";
import { DreamSearchForBlogDocument } from "./generated";
import { gql } from "graphql-tag";
import { isDegradableGeocodeError } from "@portal/app/_common/lib/clientErrorUtils";
import { DreamSearchCarousel } from "../../_components/DreamSearchCarousel";

import { EsyohWidget } from "@portal/app/_components/ui-elements/EsyohWidget";
import type { BlogRichText } from "../../payloadRichText";
import { splitBlogRichTextContent } from "../../payloadRichText";
import { separateAndFormatBlogContent } from "../../_components/splitAndFormatBlogContent";
import { DreamMarkdownReader } from "@portal/app/_components/ui-elements/DreamMarkdown/DreamMarkdownReader";
import { BlogRichTextReader } from "./BlogRichTextReader";

void gql`
  query dreamSearchForBlog(
    $dreamSearchFilters: DreamSearchFilters!
    $geo: DreamSearchGeo!
    $first: Int!
    $after: Int
    $complianceExclusionTags: [String!]!
  ) {
    dreamSearchItemsConnection(
      filter: $dreamSearchFilters
      geo: $geo
      first: $first
      after: $after
      complianceExclusionTags: $complianceExclusionTags
      includeFilterSettings: false
      includeOtherRecommendedItems: true
    ) {
      edges {
        cursor
        node {
          ...DreamSearchItemFragment
        }
      }
      otherRecommendedItems {
        ...DreamSearchItemFragment
      }
      city
      state
      zip
      totalCount
      pageInfo {
        hasNextPage
      }
    }
  }
`;

export const BlogContent = ({
  zip,
  content,
  industry,
  vocationId,
  vocationName,
  variables,
}: {
  zip: string | undefined | null;
  content: string | BlogRichText;
  industry: GQL.VocationIndustry;
  vocationId: string | undefined | null;
  vocationName: string | undefined | null;
} & Pick<BlogPageProps, "variables">) => {
  const router = useRouter();
  const { data: dreamSearchData, error: dreamSearchError } = useSuspenseQuery(
    DreamSearchForBlogDocument,
    {
      variables,
      errorPolicy: "all",
    }
  );

  // On INVALID_ADDRESS / GEOCODE_UNAVAILABLE, gracefully hide dream search
  // recommendations instead of redirecting the user away from the blog post.
  const hasInvalidAddress = isDegradableGeocodeError(dreamSearchError);

  const nodes = dreamSearchData?.dreamSearchItemsConnection?.edges?.map(({ node }) => node);
  const otherRecommendedItems = dreamSearchData?.dreamSearchItemsConnection?.otherRecommendedItems;
  const recommendations = [...(nodes ?? []), ...(otherRecommendedItems ?? [])];
  const hasRecommendations = !hasInvalidAddress && recommendations.length >= 1;
  const hasMoreRecommendations = recommendations.slice(4, 8).length >= 1;

  const isPayloadContent = typeof content !== "string";
  const payloadSections = isPayloadContent ? splitBlogRichTextContent(content) : null;
  const markdownSections = !isPayloadContent ? separateAndFormatBlogContent(content || "") : null;
  const payloadContentMiddle = payloadSections?.[1];
  const payloadContentBottom = payloadSections?.[2];
  const markdownContentMiddle = markdownSections?.[1];
  const markdownContentBottom = markdownSections?.[2];
  const hasBottomContent = isPayloadContent
    ? !!payloadContentBottom?.root.children.length
    : !!markdownContentBottom;

  const handleClickSeeMore = () =>
    router.push(
      getUrls().DreamSearch({
        industry: industry ?? "HEALTH",
        zip,
      })
    );

  const hasExactMatches = nodes && nodes.length > 0;
  const title =
    vocationName && hasExactMatches ? `${vocationName} programs for you` : "Programs for you";

  return (
    <>
      {hasRecommendations && (
        <>
          <EsyohWidget vocationId={vocationId ?? undefined} />
          <CarouselContainer>
            <DreamSearchCarousel
              title={title}
              titleComponent="span"
              programHeadingComponent="span"
              items={recommendations.slice(0, 4)}
              handleClickSeeMore={handleClickSeeMore}
              mixpanelEventMap={{
                cardClicked: "Blog - Clicked Carousel Dreamsearch Card",
                seeMoreClicked: "Blog - Clicked Carousel Dreamsearch See More",
              }}
            />
          </CarouselContainer>
        </>
      )}
      {isPayloadContent &&
        payloadContentMiddle &&
        payloadContentMiddle.root.children.length > 0 && (
          <BlogRichTextReader
            content={payloadContentMiddle}
            data-testid="blog-page-content-middle"
          />
        )}
      {!isPayloadContent && !!markdownContentMiddle && (
        <DreamMarkdownReader
          value={markdownContentMiddle}
          data-testid="blog-page-content-middle"
          tableLayout="fit"
        />
      )}
      {hasBottomContent && !hasInvalidAddress && hasMoreRecommendations && (
        <CarouselContainer>
          <DreamSearchCarousel
            title="More programs to explore"
            titleComponent="span"
            programHeadingComponent="span"
            items={recommendations.slice(4, 8)}
            handleClickSeeMore={handleClickSeeMore}
            mixpanelEventMap={{
              cardClicked: "Blog - Clicked Carousel Dreamsearch Card",
              seeMoreClicked: "Blog - Clicked Carousel Dreamsearch See More",
            }}
          />
        </CarouselContainer>
      )}
    </>
  );
};
const CarouselContainer = styled(Box)`
  width: 100vw;
`;
